import React from 'react';

import { IApiResult, IUserData, IUserFetchResult } from '../model/api';
import { useStateWithStorage } from '../utils/storage';
import { ApiContext } from './apicontext';

export enum SessionInitState {
	Failed = -1,
	Uninitialized,
	LoadedFromStorage,
	FetchingProfile,
	Ready
}

export interface ISessionContext {
	initState: SessionInitState;
	session: string;
	setSession: (session: string) => void;
	user: IUserData | undefined;
	setUser: (user: IUserData | undefined) => void;
}

const defaultSession: ISessionContext = {
	initState: SessionInitState.Uninitialized,
	session: '',
	setSession: () => {},
	user: undefined,
	setUser: () => {}
};

export const SessionContext = React.createContext<ISessionContext>(defaultSession);

type SessionProviderProps = {
	children: React.ReactNode;
};

export const SessionProvider = (props: SessionProviderProps) => {
	const Api = React.useContext(ApiContext);
	const { children } = props;

	const [initState, setInitState] = React.useState<SessionInitState>(SessionInitState.Uninitialized);
	const [session, setSession] = useStateWithStorage<string>(
		'session',
		'',
		{
			rememberForever: true,
			onInitialize: () => setInitState(SessionInitState.LoadedFromStorage)
		}
	);
	const [user, setUser] = useStateWithStorage<IUserData | undefined>('user', undefined);

	React.useEffect(() => {
		// Listen for session changes from other tabs
		window.addEventListener('storage', handleStorageEvent);
		// Cleanup
		return () => {
			window.removeEventListener('storage', handleStorageEvent);
		};

		function handleStorageEvent(event: StorageEvent): void {
			if (event.key === 'session') {
				// Log out
				if (event.oldValue !== '' && !event.newValue) {
					setSession('');
				}
				// Session changed (e.g. log in)
				else if (event.newValue) {
					setSession(JSON.parse(event.newValue));
				}
			}
		}
	}, [setSession]);

	React.useEffect(() => {
		if (initState < SessionInitState.LoadedFromStorage) return;

		if (session === '') {
			// Clear user if at this point there's somehow a user without a session
			if (user) setUser(undefined);
			setInitState(SessionInitState.Ready);
			return;
		}

		// Fetch user for session
		if (!user) {
			// Stop here if fetch already in progress
			if (initState === SessionInitState.FetchingProfile) return;
			const headers = {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${session}`
			};
			Api.fetch('user/public',
				{ headers },
				(result: IApiResult) => {
					// Valid user session located => Store user
					if (result.response === 1) {
						setUser((result as IUserFetchResult).user);
						setInitState(SessionInitState.Ready);
					}
					// Response == 0: No valid session match => Clear session and user
					else if (result.response === 0) {
						setSession('');
						setUser(undefined);
						setInitState(SessionInitState.Ready);
					}
					// Response == -1: API unavailable. Do not clear session
					//	Will try again on next visit or page reload
					else {
						setInitState(SessionInitState.Failed);
					}
				}
			);
			setInitState(SessionInitState.FetchingProfile);
			return;
		}

		// Session and user are set
		setInitState(SessionInitState.Ready);
	}, [initState, session]);

	const providerValue: ISessionContext = {
		initState,
		session,
		setSession,
		user,
		setUser
	};

	return (
		<SessionContext.Provider value={providerValue}>
			{children}
		</SessionContext.Provider>
	);
};
